<template>
  <aside class="blog-sidebar" aria-label="Sidebar du blog">
    <section class="blog-sidebar__section">
      <h2 class="blog-sidebar__title">Rechercher</h2>
      <form class="blog-sidebar__search" @submit.prevent="submitSearch">
        <input
          v-model="localSearch"
          type="search"
          class="blog-sidebar__input"
          placeholder="Rechercher dans le blog"
          autocomplete="off"
        />
        <div class="blog-sidebar__search-actions">
          <button type="submit" class="blog-sidebar__button">Rechercher</button>
          <button
            v-if="localSearch"
            type="button"
            class="blog-sidebar__button blog-sidebar__button--secondary"
            @click="clearSearch"
          >
            Effacer
          </button>
        </div>
      </form>
    </section>

    <section class="blog-sidebar__section">
      <div class="blog-sidebar__section-header">
        <h2 class="blog-sidebar__title">Catégories</h2>
        <router-link
          class="blog-sidebar__all-link"
          :class="{ 'is-active': !activeCategorySlug }"
          :to="buildAllArticlesLink()"
        >
          Tous les articles
        </router-link>
      </div>

      <div v-if="categories.length" class="blog-sidebar__categories">
        <router-link
          v-for="category in categories"
          :key="category.slug"
          class="blog-sidebar__category-link"
          :class="{ 'is-active': activeCategorySlug === category.slug }"
          :to="buildCategoryLink(category.slug)"
        >
          <span>{{ category.title }}</span>
          <strong>{{ category.article_count }}</strong>
        </router-link>
      </div>
      <p v-else class="blog-sidebar__empty">Aucune catégorie disponible.</p>
    </section>

    <section class="blog-sidebar__section">
      <h2 class="blog-sidebar__title">Articles récents</h2>

      <div v-if="recentArticles.length" class="blog-sidebar__recent-list">
        <router-link
          v-for="article in recentArticles"
          :key="article.slug"
          class="blog-sidebar__recent-item"
          :to="{ name: 'blog-article', params: { articleSlug: article.slug } }"
        >
          <img
            v-if="article.featured_image"
            :src="mediaSrc(article.featured_image)"
            :alt="article.title"
            class="blog-sidebar__recent-image"
          />
          <div v-else class="blog-sidebar__recent-image blog-sidebar__recent-image--fallback">
            {{ article.title }}
          </div>

          <div class="blog-sidebar__recent-content">
            <p class="blog-sidebar__recent-category">{{ article.category.title }}</p>
            <h3 class="blog-sidebar__recent-title">{{ article.title }}</h3>
          </div>
        </router-link>
      </div>
      <p v-else class="blog-sidebar__empty">Aucun article récent disponible.</p>
    </section>
  </aside>
</template>

<script>
export default {
  name: "BlogSidebar",
  props: {
    categories: {
      type: Array,
      default: () => [],
    },
    recentArticles: {
      type: Array,
      default: () => [],
    },
    searchValue: {
      type: String,
      default: "",
    },
    activeCategorySlug: {
      type: String,
      default: "",
    },
    currentSearchQuery: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      localSearch: this.searchValue,
    };
  },
  watch: {
    searchValue(nextValue) {
      this.localSearch = nextValue;
    },
  },
  methods: {
    submitSearch() {
      this.$emit("search", this.localSearch.trim());
    },
    clearSearch() {
      this.localSearch = "";
      this.$emit("search", "");
    },
    buildAllArticlesLink() {
      const query = {};
      if (this.currentSearchQuery) {
        query.q = this.currentSearchQuery;
      }

      return {
        name: "blog-list",
        query,
      };
    },
    buildCategoryLink(categorySlug) {
      const query = {};
      if (this.currentSearchQuery) {
        query.q = this.currentSearchQuery;
      }

      return {
        name: "blog-category",
        params: { categorySlug },
        query,
      };
    },
    mediaSrc(uri) {
      if (!uri) return null;
      if (uri.startsWith("http")) return uri;
      return `${process.env.VUE_APP_BASE_URL}${uri}`;
    },
  },
};
</script>

<style scoped>
.blog-sidebar {
  display: flex;
  flex-direction: column;
  gap: 20px;
  position: sticky;
  top: 126px;
}

.blog-sidebar__section {
  padding: 24px;
  border-radius: 18px;
  background: #f5f1e8;
}

.blog-sidebar__section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.blog-sidebar__title {
  margin: 0 0 16px;
  color: #004c40;
  font-size: 20px;
}

.blog-sidebar__section-header .blog-sidebar__title {
  margin-bottom: 0;
}

.blog-sidebar__all-link {
  color: #004c40;
  font-size: 14px;
  font-weight: 700;
  text-decoration: none;
}

.blog-sidebar__all-link.is-active {
  color: #e57b57;
}

.blog-sidebar__search {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.blog-sidebar__input {
  min-height: 46px;
  padding: 10px 12px;
  border: 1px solid #d8ddd2;
  border-radius: 12px;
  color: #35524a;
  background: #fff;
}

.blog-sidebar__search-actions {
  display: flex;
  gap: 10px;
}

.blog-sidebar__button {
  min-height: 42px;
  padding: 0 14px;
  border: 1px solid #004c40;
  border-radius: 12px;
  background: #004c40;
  color: #fff;
  font-weight: 700;
  cursor: pointer;
}

.blog-sidebar__button--secondary {
  background: transparent;
  color: #004c40;
}

.blog-sidebar__categories {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 16px;
}

.blog-sidebar__category-link {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid #d8ddd2;
  border-radius: 12px;
  background: #fff;
  color: #35524a;
  font-size: 14px;
  font-weight: 700;
  text-decoration: none;
}

.blog-sidebar__category-link.is-active {
  border-color: #004c40;
  background: rgba(0, 76, 64, 0.06);
  color: #004c40;
}

.blog-sidebar__recent-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.blog-sidebar__recent-item {
  display: flex;
  gap: 12px;
  color: inherit;
  text-decoration: none;
}

.blog-sidebar__recent-image {
  width: 88px;
  height: 88px;
  border-radius: 12px;
  object-fit: cover;
  background: #e8efe8;
  flex: 0 0 auto;
}

.blog-sidebar__recent-image--fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
  background: linear-gradient(135deg, rgba(181, 208, 136, 0.9), rgba(0, 76, 64, 0.9));
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.3;
  text-align: center;
}

.blog-sidebar__recent-content {
  min-width: 0;
}

.blog-sidebar__recent-category {
  margin: 0 0 6px;
  color: #e57b57;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.blog-sidebar__recent-title {
  margin: 0;
  color: #004c40;
  font-size: 15px;
  line-height: 1.35;
}

.blog-sidebar__empty {
  margin: 0;
  color: #6a7a74;
  font-size: 14px;
}

@media only screen and (max-width: 1023px) {
  .blog-sidebar {
    position: static;
  }
}

@media only screen and (max-width: 599px) {
  .blog-sidebar__section {
    padding: 18px;
  }

  .blog-sidebar__section-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .blog-sidebar__search-actions {
    flex-direction: column;
  }
}
</style>
